// aiInspect.js — INSPECT & ASK (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1).
//
// Nika 2026-10-04: "… and also option to inspect and ask".
//
// The composer's focus panel starts it ("Inspect & ask"). While it is on, the
// page shows an outline under the pointer around the region it would take
// (components/AiInspectLayer.js); a click takes that region as a context
// attachment of the composer — its title, its visible words and figures, a
// widget's id and params, the record ids the module marks — and the next ask
// is about it. Escape leaves; the arrow keys walk the regions and Enter takes
// one, so the mode works without a pointer.
//
// WHAT IS A REGION (the module contract, documented in the developers docs,
// "Inspect & ask"):
//   • data-inspect="<title or bundle key>" on a module element — an explicit
//     region (`dataInspect: 'valWFleetTitle'` in DOMQL); `data-inspect=""`
//     opts in with the title read from the region itself;
//   • data-inspect-record="<collection>:<id>[,…]" on an element inside a
//     region — the records it shows (rows, list items);
//   • data-inspect="false" — a PRIVATE subtree, at any depth: never taken
//     (not even an explicit region inside it; the region, if any, is the
//     nearest ancestor above it), and when a region above it is taken, its
//     words, figures and record ids never ride the ask;
//   • every home widget frame is a region by default (its widget id, app and
//     params ride along);
//   • a module page that marks nothing still answers: the nearest SECTION /
//     ARTICLE / role="region", else the nearest card surface (its own fill and
//     radius) — never smaller than 120 × 40, never most of the screen.
// Only the routed page (`content`), the page sheet (`PageSlot`) and the main
// layer over a page (`AssistantMainLayer` — a Fly page, a board, the thread
// off the home: a Fly page itself can be inspected, Fly train 2) are
// inspected; the shell's chrome (header, composer, rails, overlays) never is.
//
// THE CAPTURE rides the composer's picked context (`root.pickedElements`):
// one entry per region with its facts on `inspect`. That array is declared,
// wiped on a workspace switch, rendered as a chip in the composer and under
// the sent line, and cleared after a send — the whole lifecycle exists
// already. simoneAiTransport lifts the facts into `context.inspected` (the
// server's INSPECTED line) instead of the 80-character pointing line.
//
// Non-reactive handles (the hovered region, the keyboard list) live on the
// root state object under `__aiInspect*` — the root.__navbarSaveQueue
// pattern; the reactive pair is `_aiInspect` (on/off) and `_aiInspectBox`
// (the outline).

const _AII_TEXT_MAX = 1800
const _AII_RECORDS_MAX = 20
const _AII_REGIONS_MAX = 3
// The root children whose subtree is inspectable: the routed page, the page
// sheet and the main layer over a page (the Fly canvas, a board, the thread
// off the home). Everything else directly under the shell root is chrome.
const _AII_PAGE_KEYS = ['content', 'PageSlot', 'AssistantMainLayer']
const _AII_CONTROL_TAGS = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL', 'OPTION']

const _aiiStr = (v) => (v == null ? '' : String(v))
const _aiiClean = (v) => _aiiStr(v).replace(/\s+/g, ' ').trim()
const _aiiRootOf = (el) => (el && el.state && el.state.root) || null
const _aiiTopOf = (el) => {
  try {
    return el && typeof el.getRoot === 'function' ? el.getRoot() : null
  } catch (_) {
    return null
  }
}
const _aiiWin = () => (typeof window !== 'undefined' ? window : null)

// The DOMQL element a DOM node belongs to (a text node, an icon's <use> and a
// node DOMQL did not create walk up to the nearest one that carries `.ref`).
const _aiiElementOfNode = (node) => {
  let n = node
  while (n && !n.ref) n = n.parentNode
  return n ? n.ref : null
}

// Is this element a region, and of which kind: 'explicit' | 'widget' |
// 'section' | null.
const _aiiKind = (el) => {
  const node = el && el.node
  if (!node || node.nodeType !== 1) return null
  const ds = node.dataset || {}
  if (ds.inspect != null && ds.inspect !== 'false') return 'explicit'
  if (ds.widgetId && node.classList && node.classList.contains('home-grid-item')) return 'widget'
  // A Fly page's widget or view section (components/AssistantFlyView.js
  // AssistantFlyCard `data-fly-widget`): a widget region too — its id, app
  // and params ride along.
  if (ds.flyWidget) return 'widget'
  if (_AII_CONTROL_TAGS.includes(node.tagName)) return null
  if (typeof node.getBoundingClientRect !== 'function') return null
  const r = node.getBoundingClientRect()
  if (r.width < 120 || r.height < 40) return null
  const win = _aiiWin()
  if (win && r.width * r.height > 0.6 * win.innerWidth * win.innerHeight) return null
  const role = node.getAttribute && node.getAttribute('role')
  if (node.tagName === 'SECTION' || node.tagName === 'ARTICLE' || role === 'region') {
    return 'section'
  }
  if (!win || typeof win.getComputedStyle !== 'function') return null
  const cs = win.getComputedStyle(node)
  const bg = _aiiStr(cs.backgroundColor)
  const filled = !!bg && bg !== 'transparent' && !/rgba\(\s*0,\s*0,\s*0,\s*0\s*\)/.test(bg)
  const rounded = parseFloat(cs.borderTopLeftRadius) > 0
  return filled && rounded ? 'section' : null
}

// THE RESOLVER: the region a DOM node sits in, or null (chrome, an opted-out
// subtree with nothing above it, or no region before the page boundary).
// → { el, kind }
export const aiInspectRegionOf = function aiInspectRegionOf(target) {
  let el = _aiiElementOfNode(target)
  // The shell root ELEMENT (el.getRoot()). Live, it is not parentless: the
  // framework wraps it (`{ smblsapp }`), so "the parent has no parent" would
  // stop one level too high and take nothing (measured on dev 2026-10-04).
  // A tree with no root pointer (a bare test tree) keeps that rule.
  const top = _aiiTopOf(el)
  let region = null
  while (el) {
    const node = el.node
    const ds = (node && node.dataset) || {}
    // An opted-out subtree (data-inspect="false") is never taken — not even an
    // explicit region inside it: the nearest region ABOVE it is (privacy wins
    // at every depth; its words never ride, see _aiiRead).
    if (ds.inspect === 'false') region = null
    if (!region && ds.inspect !== 'false') {
      const kind = _aiiKind(el)
      if (kind) region = { el, kind }
    }
    const parent = el.parent
    // A child of the shell root: the page or the sheet bound the search;
    // anything else there is chrome.
    if (parent && (top ? parent === top : !parent.parent)) {
      return _AII_PAGE_KEYS.includes(el.key) ? region : null
    }
    if (top && el === top) return null
    el = parent
  }
  return null
}

// Is this DOM element opted out of Inspect — the module contract's PRIVATE
// marker, `data-inspect="false"` (a host name, an IP, a private note)?
const _aiiOptedOut = (n) => !!(n && n.nodeType === 1 && n.dataset && n.dataset.inspect === 'false')

// THE ONE READ of a region's rendered tree — what a person can SEE in it:
// its words (text nodes in document order, capped), the record ids it marks
// (`data-inspect-record`, capped) and its first heading. It skips what is
// hidden, inert or aria-hidden (a widget's resting edit controls) and EVERY
// opted-out subtree at any depth: a region above a private child is taken,
// the child's words, figures and record ids never are (privacy finding,
// Validators node page's Host card, 2026-10-05). A READ of the rendered tree
// (like the outline's measurement): what a module's props and html rendered
// is only there, never in the DOMQL element tree. Nothing here writes to a
// node. → { text, records, heading }
const _aiiRead = (node, max) => {
  const parts = []
  const records = []
  let len = 0
  let headingNode = null
  const walk = (n, isRoot) => {
    if (!n) return
    if (n.nodeType === 3) {
      if (len >= max) return
      const t = _aiiClean(n.nodeValue)
      if (t) {
        parts.push(t)
        len += t.length + 3
      }
      return
    }
    if (n.nodeType !== 1) return
    if (_aiiOptedOut(n)) return
    const tag = n.tagName
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEMPLATE') return
    if (n.hidden || (n.hasAttribute && n.hasAttribute('inert'))) return
    if (n.getAttribute && n.getAttribute('aria-hidden') === 'true') return
    if (typeof n.getClientRects === 'function' && n.getClientRects().length === 0) return
    const marked = n.dataset ? n.dataset.inspectRecord : null
    if (marked) {
      for (const one of _aiiStr(marked).split(',')) {
        const t = one.trim()
        if (t && records.length < _AII_RECORDS_MAX && !records.includes(t)) records.push(t)
      }
    }
    if (
      !headingNode &&
      !isRoot &&
      (/^H[1-6]$/.test(tag) || (n.getAttribute && n.getAttribute('role') === 'heading'))
    ) {
      headingNode = n
    }
    for (const c of n.childNodes || []) walk(c, false)
  }
  walk(node, true)
  const heading = headingNode ? _aiiRead(headingNode, 80).text : ''
  return { text: parts.join(' · ').slice(0, max), records, heading }
}

// A region's title: its own declared one, a widget's, its accessible name,
// its first heading, else its first visible words — the last two from the
// same read, so never from an opted-out subtree.
const _aiiTitleOf = (el, kind, say) => {
  const node = el.node
  const ds = (node && node.dataset) || {}
  if (kind === 'explicit' && ds.inspect && ds.inspect !== 'true') return _aiiClean(say(ds.inspect))
  if (kind === 'widget' && el.state && el.state.title) return _aiiClean(say(el.state.title))
  const aria = node.getAttribute && node.getAttribute('aria-label')
  if (aria) return _aiiClean(aria)
  const read = _aiiRead(node, 160)
  if (read.heading) return read.heading.slice(0, 80)
  const first = read.text.split(' · ')[0] || ''
  return first.slice(0, 80)
}

// Plain data from a DOMQL state value (a nested state proxy hands its values
// over through parse()).
const _aiiPlain = (v) => {
  if (!v || typeof v !== 'object') return v
  const p = typeof v.parse === 'function' ? v.parse() : v
  try {
    return JSON.parse(JSON.stringify(p))
  } catch (_) {
    return undefined
  }
}

// THE FACTS of a region — what rides the ask (`context.inspected[]`).
export const aiInspectFacts = function aiInspectFacts(region) {
  const el = this
  const r = region && region.el ? region : null
  if (!r || !r.el.node) return null
  const say = (key) => {
    try {
      return typeof el.call === 'function' ? _aiiStr(el.call('polyglot', key)) || key : key
    } catch (_) {
      return key
    }
  }
  const root = _aiiRootOf(el) || {}
  const node = r.el.node
  const title = _aiiTitleOf(r.el, r.kind, say) || say('workspace.aiInspect.untitled')
  const facts = {
    id: 'region-' + Date.now().toString(36),
    title,
    kind: r.kind === 'widget' ? 'widget' : 'section'
  }
  let route = ''
  try {
    route = _aiiStr(el.call('aiScopeSurface', root).path)
  } catch (_) {
    route = ''
  }
  if (route) facts.route = route
  if (r.kind === 'widget') {
    const s = r.el.state || {}
    facts.widget = { id: _aiiStr(s.id) }
    if (s.moduleId) facts.app = { id: _aiiStr(s.moduleId) }
    // The params it was asked with, then what its mounted view RESOLVED over
    // them (a Fly section's view — the views contract's `resolvedParams`,
    // functions/assistantAppViews.js): a site card names the site it shows.
    const body = r.el.CardBody && r.el.CardBody.content
    const bag = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})
    const params = {
      ...bag(_aiiPlain(s.params)),
      ...bag(body ? _aiiPlain(body.resolvedParams) : null)
    }
    if (Object.keys(params).length) {
      facts.widget.params = params
    }
    if (s.describe) facts.widget.describe = _aiiStr(s.describe).slice(0, 240)
  } else {
    try {
      const sf = el.call('aiScopeSurface', root)
      if (sf && sf.app && sf.app.module) facts.app = { id: sf.app.id, name: sf.app.name }
    } catch (_) {
      /* no scope reader on this host */
    }
  }
  // Words and record ids from the one read: never from an opted-out subtree.
  const read = _aiiRead(node, _AII_TEXT_MAX)
  if (read.records.length) facts.records = read.records
  facts.text = read.text
  return facts
}

// The outline's box for a region (viewport px — a MEASUREMENT, the one place
// raw px are the value), with its label.
const _aiiBox = (region, label) => {
  const r = region.el.node.getBoundingClientRect()
  return {
    x: Math.round(r.left),
    y: Math.round(r.top),
    w: Math.round(r.width),
    h: Math.round(r.height),
    label: _aiiStr(label)
  }
}

const _aiiShow = (el, root, region) => {
  root.__aiInspectRegion = region || null
  if (!region) {
    if (root._aiInspectBox) root.update({ _aiInspectBox: null }, { preventFetch: true })
    return
  }
  const say = (key) => _aiiStr(el.call('polyglot', key)) || key
  const label = _aiiTitleOf(region.el, region.kind, say)
  root.update({ _aiInspectBox: _aiiBox(region, label) }, { preventFetch: true })
}

// The composer's textarea, to hand focus back to.
const _aiiComposerField = (el) => {
  const top = _aiiTopOf(el)
  const composer = top && top.CanvasPromptTextarea
  const field = composer && composer.Textarea
  return field && field.node ? field.node : null
}

// ON. The panel steps down, the outline follows the pointer, and focus moves
// to the layer's hint (role=status) so the keys reach the mode.
export const aiInspectStart = function aiInspectStart() {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || typeof root.update !== 'function') return false
  root.__aiInspectRegion = null
  root.__aiInspectList = null
  root.__aiInspectIndex = -1
  root.update(
    { _aiInspect: true, _aiInspectBox: null, _aiPanel: 'dismissed', _promptFocused: false },
    { preventFetch: true }
  )
  // The layer mounts on this update (if-gated: no open-edge hook), so the
  // focus move polls briefly for its hint.
  const win = _aiiWin()
  if (win && typeof win.setTimeout === 'function') {
    let tries = 0
    const tick = () => {
      tries++
      const top = _aiiTopOf(el)
      const hint = top && top.AiInspectLayer && top.AiInspectLayer.InspectHint
      if (hint && hint.node && typeof hint.node.focus === 'function') {
        hint.node.focus()
        return
      }
      if (tries < 10) win.setTimeout(tick, 30)
    }
    win.setTimeout(tick, 0)
  }
  return true
}

// OFF — with a pick (focus to the composer, the panel stays down so the chip
// and the words are what the member sees) or without (Escape, Cancel).
export const aiInspectStop = function aiInspectStop() {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || typeof root.update !== 'function') return false
  root.__aiInspectRegion = null
  root.__aiInspectList = null
  root.__aiInspectIndex = -1
  root.update({ _aiInspect: false, _aiInspectBox: null }, { preventFetch: true })
  const field = _aiiComposerField(el)
  if (field && typeof field.focus === 'function') field.focus()
  return true
}

// TAKE a region: its facts become a composer attachment (a picked entry), the
// mode ends, focus returns to the composer for the question.
export const aiInspectPick = function aiInspectPick(region) {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || typeof root.update !== 'function' || !region) return false
  const facts = aiInspectFacts.call(el, region)
  if (!facts) return false
  const entry = {
    keyPath: [facts.title],
    key: facts.title,
    tag: 'region',
    title: facts.title,
    text: _aiiStr(facts.text).slice(0, 80),
    pickedAt: Date.now(),
    inspect: facts
  }
  const prev = (Array.isArray(root.pickedElements) ? root.pickedElements : []).filter(
    (p) => !(p && p.inspect && p.inspect.title === facts.title && p.inspect.route === facts.route)
  )
  const regions = prev.filter((p) => p && p.inspect)
  const keep = regions.length >= _AII_REGIONS_MAX ? prev.filter((p) => p !== regions[0]) : prev
  root.update({ pickedElements: [...keep, entry], _aiPanel: 'dismissed' })
  aiInspectStop.call(el)
  return true
}

// Pointer over the page: outline the region under it (only when it changes).
export const aiInspectHover = function aiInspectHover(e) {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || !root._aiInspect || !e) return
  if (el.node && typeof el.node.contains === 'function' && el.node.contains(e.target)) return
  const region = aiInspectRegionOf(e.target)
  const cur = root.__aiInspectRegion
  if ((cur && cur.el) === (region && region.el)) return
  _aiiShow(el, root, region)
}

// The outline follows its region through a scroll or a resize.
export const aiInspectRefresh = function aiInspectRefresh() {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || !root._aiInspect) return
  const cur = root.__aiInspectRegion
  if (!cur || !cur.el || !cur.el.node || !cur.el.node.isConnected) {
    _aiiShow(el, root, null)
    return
  }
  _aiiShow(el, root, cur)
}

// A press on the page while inspecting belongs to the mode, never the page
// (a widget would start a drag, a row would open its record). The layer's own
// hint keeps its clicks.
export const aiInspectSwallow = function aiInspectSwallow(e) {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || !root._aiInspect || !e) return false
  if (el.node && typeof el.node.contains === 'function' && el.node.contains(e.target)) return false
  if (typeof e.preventDefault === 'function') e.preventDefault()
  if (typeof e.stopPropagation === 'function') e.stopPropagation()
  return true
}

// A click on the page: take the region under it (or nothing — the mode stays
// on until a region is taken or the member leaves).
export const aiInspectClick = function aiInspectClick(e) {
  const el = this
  if (!aiInspectSwallow.call(el, e)) return false
  const region = aiInspectRegionOf(e.target)
  if (!region) return true
  return aiInspectPick.call(el, region)
}

// Is this element inside (or itself) an opted-out subtree, below `host`?
const _aiiInsideOptOut = (el, host) => {
  let e = el
  while (e && e !== host) {
    if (_aiiOptedOut(e.node)) return true
    e = e.parent
  }
  return false
}

// The keyboard list: every region of the page in document order, visible in
// the viewport, never inside an opted-out subtree (built once per walk, on
// the first arrow).
const _aiiCandidates = (el) => {
  const top = _aiiTopOf(el)
  if (!top) return []
  const win = _aiiWin()
  const vh = win ? win.innerHeight : 0
  const out = []
  for (const key of _AII_PAGE_KEYS) {
    const host = top[key]
    if (!host || typeof host.lookdownAll !== 'function') continue
    // A host that is not on screen offers nothing: the main layer while it is
    // closed, the page while the layer is over it (both keep their box,
    // hidden — app.js, the AssistantMainLayer rule).
    const hostNode = host.node
    if (
      hostNode &&
      win &&
      typeof win.getComputedStyle === 'function' &&
      win.getComputedStyle(hostNode).visibility === 'hidden'
    ) {
      continue
    }
    const found =
      host.lookdownAll((c) => {
        const kind = _aiiKind(c)
        if (!kind || _aiiInsideOptOut(c, host)) return false
        const r = c.node.getBoundingClientRect()
        return !vh || (r.bottom > 0 && r.top < vh)
      }) || []
    for (const c of found) out.push({ el: c, kind: _aiiKind(c) })
  }
  return out
}

// THE KEYS while inspecting: Escape leaves; ↓ → Tab take the next region,
// ↑ ← Shift+Tab the previous; Enter takes the outlined one.
export const aiInspectKey = function aiInspectKey(e) {
  const el = this
  const root = _aiiRootOf(el)
  if (!root || !root._aiInspect || !e) return false
  const key = e.key
  if (key === 'Escape') {
    e.preventDefault()
    e.stopPropagation()
    aiInspectStop.call(el)
    return true
  }
  const next = key === 'ArrowDown' || key === 'ArrowRight' || (key === 'Tab' && !e.shiftKey)
  const prev = key === 'ArrowUp' || key === 'ArrowLeft' || (key === 'Tab' && e.shiftKey)
  if (next || prev) {
    e.preventDefault()
    e.stopPropagation()
    if (!root.__aiInspectList) root.__aiInspectList = _aiiCandidates(el)
    const list = root.__aiInspectList
    if (!list.length) return true
    const at = typeof root.__aiInspectIndex === 'number' ? root.__aiInspectIndex : -1
    const i = next ? (at + 1) % list.length : at <= 0 ? list.length - 1 : at - 1
    root.__aiInspectIndex = i
    const region = list[i]
    const node = region.el.node
    // Bring it into view (the one scroll a keyboard walk needs).
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'nearest' })
    _aiiShow(el, root, region)
    return true
  }
  if (key === 'Enter') {
    const cur = root.__aiInspectRegion
    if (!cur) return false
    e.preventDefault()
    e.stopPropagation()
    return aiInspectPick.call(el, cur)
  }
  return false
}
